import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, map, of, tap, finalize } from 'rxjs';
import {
  SearchResult,
  ElasticsearchResponse,
  ElasticsearchHit,
  SearchOptions,
  SearchFieldFilter,
} from './search.model';
import { environment } from '../../../environments/environment';
import { LoggingService } from '../../core/services/logging.service';
@Injectable({ providedIn: 'root' })
export class SearchService {
  private http = inject(HttpClient);
  private readonly loggingService = inject(LoggingService);

  private results$ = new BehaviorSubject<SearchResult[]>([]);
  private loading$ = new BehaviorSubject<boolean>(false);
  private searched$ = new BehaviorSubject<boolean>(false);
  private totalHits$ = new BehaviorSubject<number>(0);

  currentQuery = '';
  /** Persisted filter state — survives component re-creation on layout switch */
  currentOptions: SearchOptions = {};

  readonly searchResults$ = this.results$.asObservable();
  readonly isLoading$ = this.loading$.asObservable();
  readonly hasSearched$ = this.searched$.asObservable();
  readonly totalResults$ = this.totalHits$.asObservable();

  /** Map UI filter keys to actual ES field names */
  private static readonly FIELD_MAP: Record<SearchFieldFilter, string[]> = {
    customerId: ['customerId^5'],
    companyName: ['companyName'],
    externId: ['externID'],
    phoneServiceId: ['phoneServiceId'],
    userAccountId: ['userAccountId'],
    name: ['firstName', 'lastName', 'contractFirstName', 'contractLastName', 'fullName', 'contractFullName'],
    login: ['login', 'email'],
  };

  search(query: string, options?: SearchOptions): Observable<SearchResult[]> {
    if (!query?.trim()) {
      this.results$.next([]);
      this.totalHits$.next(0);
      return this.results$;
    }

    this.currentQuery = query;
    this.currentOptions = options ?? {};
    this.loading$.next(true);
    this.searched$.next(true);

    return this.runEsQuery(query, options).pipe(
      tap((response) => {
        this.totalHits$.next(response.hits.total);
        /**
         * @ticket EB-3798
         * Usage: one `search.query` per executed classic (AG-Grid) search.
         * `quickSearch()` (typeahead) deliberately does NOT emit.
         */
        this.loggingService.usage('search.query', {
          resultCount: response.hits.total,
          // (no `zeroResult` — Graylog expresses it as `resultCount:0`; a second
          // derived field only adds drift. PR #43 review.)
          mode: 'classic',
        });
      }),
      map((response) => this.mapHitsToResults(response.hits.hits)),
      tap((results) => this.results$.next(results)),
      finalize(() => this.loading$.next(false)),
    );
  }

  /**
   * Stateless variant of `search()` for autocomplete / typeahead consumers (e.g. the calls
   * view's IdAutocompleteComponent). Issues the same Elasticsearch query but does NOT touch
   * the shared BehaviorSubjects (`results$`, `loading$`, `searched$`, `totalHits$`,
   * `currentQuery`, `currentOptions`) that drive the global search UI on the landing page.
   *
   * Without this isolation the global search's filter chips would be reset every time the
   * autocomplete fires a request — see EB-3641 follow-up.
   */
  quickSearch(query: string, options?: SearchOptions): Observable<SearchResult[]> {
    if (!query?.trim()) return of([]);
    return this.runEsQuery(query, options).pipe(
      map((response) => this.mapHitsToResults(response.hits.hits)),
    );
  }

  /** Build + execute the ES request. Pure function over (query, options) — no side effects. */
  private runEsQuery(query: string, options?: SearchOptions): Observable<ElasticsearchResponse> {
    let escapedQuery = query.replace(/\+/g, '\\+');
    if (options?.autoWildcard) {
      // Append `*` to every whitespace-separated term so partial typing matches.
      // Skip terms that already contain `*` or `?` to avoid double-wildcards.
      escapedQuery = escapedQuery
        .split(/\s+/)
        .filter((t) => t.length > 0)
        .map((t) => (/[*?]/.test(t) ? t : `${t}*`))
        .join(' ');
    }

    const fields = this.buildFieldsList(options?.fieldFilters);

    const boolQuery: Record<string, unknown> = {
      must: {
        simple_query_string: {
          query: escapedQuery,
          fields,
          lenient: true,
          default_operator: 'and',
        },
      },
      should: [
        { term: { _type: { value: 'customer', boost: 5 } } },
        { term: { _type: { value: 'useraccount', boost: 3 } } },
      ],
      must_not: [
        { term: { _type: 'caiprofile' } },
        { term: { _type: 'caisuggestion' } },
      ],
    };

    const filters: Record<string, unknown>[] = [];
    if (options?.entityFilters?.length) {
      filters.push({ terms: { _type: options.entityFilters } });
    }
    if (options?.brandFilters?.length) {
      filters.push({ terms: { brand: options.brandFilters } });
    }
    if (filters.length) {
      boolQuery['filter'] = filters;
    }

    const body = {
      query: { bool: boolQuery },
      highlight: { fields: { '*': {} }, require_field_match: false },
      size: 1000,
    };

    return this.http.post<ElasticsearchResponse>(
      `${environment.searchUrl}/search`,
      body,
    );
  }

  private buildFieldsList(filters?: SearchFieldFilter[]): string[] {
    if (!filters?.length) return ['customerId^5', '*'];
    const fields: string[] = [];
    for (const f of filters) {
      fields.push(...(SearchService.FIELD_MAP[f] ?? []));
    }
    return fields.length ? fields : ['customerId^5', '*'];
  }

  clearResults(): void {
    this.currentQuery = '';
    this.results$.next([]);
    this.totalHits$.next(0);
    this.searched$.next(false);
  }

  private mapHitsToResults(hits: ElasticsearchHit[]): SearchResult[] {
    return hits.map((hit) => this.mapHit(hit)).filter(Boolean) as SearchResult[];
  }

  private mapHit(hit: ElasticsearchHit): SearchResult | null {
    const src = hit._source;
    const type = hit._type;
    const status = (src['status'] as string) ?? '';

    switch (type) {
      case 'customer':
        return {
          brand: (src['brand'] as string) ?? '',
          customerId: String(src['customerId'] ?? ''),
          companyName: (src['companyName'] as string) ?? '',
          firstName: (src['contractFirstName'] as string) ?? '',
          lastName: (src['contractLastName'] as string) ?? '',
          category: `customer - ${src['customerId'] ?? ''}`,
          detail: (src['contractFullName'] as string) ?? '',
          externId: (src['externID'] as string) ?? '',
          status,
          entityType: 'customer',
          entityId: String(src['customerId'] ?? ''),
        };

      case 'phoneservice':
        return {
          brand: (src['brand'] as string) ?? '',
          customerId: String(src['customerId'] ?? ''),
          // Company = the owning customer's name, not the PS description.
          // Falls back to description if the ES doc lacks companyName so a row
          // is never empty.
          companyName: (src['companyName'] as string) ?? (src['description'] as string) ?? '',
          firstName: (src['firstName'] as string) ?? '',
          lastName: (src['lastName'] as string) ?? '',
          category: `phoneservice - ${src['phoneServiceId'] ?? ''}`,
          detail: (src['description'] as string) ?? '',
          externId: '',
          status,
          entityType: 'phoneservice',
          entityId: String(src['phoneServiceId'] ?? ''),
        };

      case 'useraccount':
        return {
          brand: (src['brand'] as string) ?? '',
          customerId: String(src['customerId'] ?? ''),
          companyName: (src['companyName'] as string) ?? '',
          firstName: (src['firstName'] as string) ?? '',
          lastName: (src['lastName'] as string) ?? '',
          category: `useraccount - ${src['userAccountId'] ?? ''}`,
          detail: (src['fullName'] as string) ?? '',
          externId: '',
          status,
          entityType: 'useraccount',
          entityId: String(src['userAccountId'] ?? ''),
        };

      case 'meetingroom':
        return {
          brand: (src['brand'] as string) ?? '',
          customerId: String(src['customerId'] ?? ''),
          companyName: (src['companyName'] as string) ?? (src['description'] as string) ?? '',
          firstName: '',
          lastName: '',
          category: `meetingroom - ${src['id'] ?? ''}`,
          detail: (src['level'] as string) ?? '',
          externId: '',
          status,
          entityType: 'meetingroom',
          entityId: String(src['id'] ?? ''),
        };

      case 'servicedoffice':
        return {
          brand: (src['brand'] as string) ?? '',
          customerId: String(src['customerId'] ?? ''),
          companyName: (src['companyName'] as string) ?? (src['description'] as string) ?? '',
          firstName: '',
          lastName: '',
          category: `servicedoffice - ${src['id'] ?? ''}`,
          detail: (src['location'] as string) ?? '',
          externId: '',
          status,
          entityType: 'servicedoffice',
          entityId: String(src['id'] ?? ''),
        };

      case 'businessaddressservice':
        return {
          brand: (src['brand'] as string) ?? '',
          customerId: String(src['customerId'] ?? ''),
          companyName: (src['companyName'] as string) ?? (src['description'] as string) ?? '',
          firstName: '',
          lastName: '',
          category: `businessaddressservice - ${src['businessAddressServiceId'] ?? ''}`,
          detail: (src['location'] as string) ?? '',
          externId: '',
          status,
          entityType: 'businessaddressservice',
          entityId: String(src['businessAddressServiceId'] ?? ''),
        };

      default:
        return null;
    }
  }
}
